/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable react/display-name */
import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { makeClassNames } from '../../../utils/styles';
import './horizontal-nav.scss';

const HorizontalNavContext = createContext({
  activeIndex: 0,
  isTab: false,
});

const classNames = (itemClassName?: string, noGap?: boolean) =>
  makeClassNames({
    horizontalNav: 'horizontalNav',
    arrow: 'horizontalNav__arrow',
    listWrapper: 'horizontalNav__list-wrapper',
    list: ['horizontalNav__list', noGap && 'horizontalNav__list--no-gap'],
    item: ['horizontalNav__item', itemClassName],
  });

export interface HorizontalNavProps extends React.HTMLAttributes<HTMLDivElement> {
  activeIndex: number;
  noGap?: boolean;
  arrows?: HorizontalNavArrowsProps;
  isTab?: boolean;
  listClassName?: string;
}

export interface HorizontalNavItemProps extends React.HTMLAttributes<HTMLButtonElement | HTMLDivElement> {
  index: number;
  hasInnerLink?: boolean;
}

export interface HorizontalNavArrowsProps {
  show: boolean;
  width: number;
  left: {
    icon: ReactNode;
    className?: string;
    onClick?: () => void;
  };
  right: {
    icon: ReactNode;
    className?: string;
    onClick?: () => void;
  };
}

export function HorizontalNav({
  children,
  activeIndex = 0,
  className = '',
  listClassName = '',
  noGap = false,
  isTab = false,
  arrows,
  ...props
}: HorizontalNavProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const scrollWrapperRef = useRef<HTMLDivElement>(null);

  const [isFirstChildVisible, setIsFirstChildVisible] = useState(activeIndex === 0);
  const [isLastChildVisible, setIsLastChildVisible] = useState(false);

  const cn = classNames(className, noGap);

  const onArrowClick = useCallback(
    (direction: 'left' | 'right') => {
      if (!listRef.current) {
        return;
      }
      const selectedItem = listRef.current.children[activeIndex] as HTMLElement | null;
      if (!selectedItem) {
        return;
      }

      const delta = direction === 'left' ? 1 : -1;

      scrollWrapperRef.current?.scrollTo({
        left: scrollWrapperRef.current.scrollLeft - selectedItem.offsetWidth * delta,
        behavior: 'smooth',
      });
    },
    [activeIndex]
  );

  const contextValue = useMemo(() => ({ activeIndex, isTab }), [activeIndex, isTab]);

  useEffect(() => {
    if (!listRef.current) {
      return;
    }
    const selectedItem = listRef.current.children[activeIndex] as HTMLElement | null;
    if (!selectedItem) {
      return;
    }

    const offset = arrows?.show ? arrows.width : 0;
    const scrollPosition = selectedItem.offsetLeft - selectedItem.offsetWidth - offset;

    scrollWrapperRef.current?.scrollTo({
      left: Math.max(0, scrollPosition),
      behavior: 'smooth',
    });

    setIsLastChildVisible(activeIndex === listRef.current.children.length - 1);
  }, [activeIndex, arrows?.show, arrows?.width]);

  useEffect(() => {
    if (!arrows?.show) {
      return;
    }
    if (!listRef.current) {
      return;
    }

    const list = listRef.current;
    const parent = scrollWrapperRef.current;
    if (!parent || list.children.length === 0) {
      return;
    }

    const options = {
      root: parent,
      rootMargin: '0px',
      threshold: 0.95,
    };

    const observeVisibility = (
      element: HTMLElement,
      callback: typeof setIsFirstChildVisible | typeof setIsLastChildVisible
    ) => {
      if (!element) {
        return null;
      }
      const observer = new IntersectionObserver(([entry]) => {
        callback(entry.isIntersecting);
      }, options);
      observer.observe(element);
      return observer;
    };

    const firstChild = list.children[0] as HTMLElement;
    const lastChild = list.children[list.children.length - 1] as HTMLElement;

    const firstChildObserver = observeVisibility(firstChild, setIsFirstChildVisible);
    const lastChildObserver = firstChild !== lastChild ? observeVisibility(lastChild, setIsLastChildVisible) : null;

    return () => {
      firstChildObserver?.disconnect();
      lastChildObserver?.disconnect();
    };
  }, [children, arrows?.show]);

  return (
    <HorizontalNavContext.Provider value={contextValue}>
      <div className={`${cn.horizontalNav} ${className}`} {...props}>
        {arrows?.show && (
          <button
            type="button"
            onClick={() => onArrowClick('left')}
            disabled={isFirstChildVisible}
            className={`${cn.arrow} ${arrows.left.className || ''}`}
            aria-label="Match precedenti"
          >
            {arrows.left.icon || '<'}
          </button>
        )}

        <div className={cn.listWrapper} ref={scrollWrapperRef}>
          <div className={`${cn.list} ${listClassName}`} ref={listRef}>
            {children}
          </div>
        </div>

        {arrows?.show && (
          <button
            type="button"
            onClick={() => onArrowClick('right')}
            disabled={isLastChildVisible}
            className={`${cn.arrow} ${arrows.right.className || ''}`}
            aria-label="Match successivi"
          >
            {arrows.right.icon || '>'}
          </button>
        )}
      </div>
    </HorizontalNavContext.Provider>
  );
}

HorizontalNav.Item = ({ index, children, className = '', onClick, hasInnerLink, ...props }: HorizontalNavItemProps) => {
  const { activeIndex, isTab } = useContext(HorizontalNavContext);
  const isActive = index === activeIndex;
  const cn = classNames(className);
  const buttonProps = hasInnerLink ? {} : { type: 'button' };
  const Tag = (hasInnerLink ? 'div' : 'button') as keyof JSX.IntrinsicElements;

  return (
    <Tag
      // @ts-ignore
      onClick={onClick}
      className={cn.item}
      data-active={isActive}
      aria-expanded={isTab ? isActive : undefined}
      {...buttonProps}
      {...props}
    >
      {children}
    </Tag>
  );
};
